import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import ElementQuery from '../../ElementQuery/ElementQuery';
import Lightbox from '../../Lightbox/Lightbox';
import Modal from '../../Modal/Modal';
import TooltipSelect from '../Select/Select';
import { getLeftOffset } from '../helpers';

import styles from './Controller.scss';
import mediaQueries from 'wanderu-css/mediaQueries.scss';
class TooltipController extends React.Component {
    static propTypes = {
        children: PropTypes.node.isRequired,
        contentClassName: PropTypes.string,
        offsetX: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        offsetY: PropTypes.number,
        noHover: PropTypes.bool,
        className: PropTypes.string,
        breakpoint: PropTypes.number,
        noModal: PropTypes.bool,
        onClick: PropTypes.func,
        onHover: PropTypes.func,
        placement: PropTypes.oneOf(['top', 'bottom']),
    };

    static defaultProps = {
        offsetX: 0,
        offsetY: 0,
        noHover: false,
        noModal: false,
        className: '',
        breakpoint: parseInt(mediaQueries.maxWidthSmallScreen, 10),
        onClick() { },
        onHover() { },
        placement: 'top',
    };

    getPositionTimeout = null;

    showTooltipTimeout = null;

    constructor(props) {
        super(props);
        this.tooltip = React.createRef();
        this.queryElement = typeof window !== 'undefined'
            ? document.body
            : null;
        this.state = {
            style: {
                position: 'absolute',
                top: 0,
                left: 0,
            },
            tooltipWidth: 0,
            queries: this._buildQueryMap(),
            useModal: true,
            useTooltip: true,
            showTooltip: false,
            showModal: false,
        };
    }

    getPosition = (elementBounding) => {
        this.getPositionTimeout = setTimeout(() => {
            const { offsetX, offsetY } = this.props;
            const { tooltipWidth } = this.state;
            const {
                top, left, height, width,
            } = elementBounding;
            const topOffset = top
                + height
                + offsetY
                + (window.pageYOffset
                    || document.documentElement.scrollTop
                    || document.body.scrollTop
                    || 0);
            this.setState((state) => ({
                style: {
                    ...state.style,
                    left: getLeftOffset({
                        offset: offsetX,
                        left,
                        width,
                        tooltipWidth,
                    }),
                    top: topOffset,
                },
            }));
        }, 0);
    };

    componentDidUpdate(prevProps, prevState) {
        this.showTooltipTimeout = setTimeout(() => {
            if (this.state.showTooltip && !prevState.showTooltip) {
                window.addEventListener('touchend', this.onMouseLeave);
                window.addEventListener('click', this.onMouseLeave);
            } else {
                window.removeEventListener('touchend', this.onMouseLeave);
                window.removeEventListener('click', this.onMouseLeave);
            }
        }, 0);

        if (prevProps.breakpoint !== this.props.breakpoint) {
            this.setState(() => ({
                queries: this._buildQueryMap(),
            }));
        }
    }

    componentWillUnmount() {
        window.removeEventListener('click', this.onMouseLeave);
        window.removeEventListener('touchend', this.onMouseLeave);
        clearTimeout(this.getPositionTimeout);
        clearTimeout(this.showTooltipTimeout);
    }

    onMouseEnter = (e, resize) => {
        if (!this.props.noHover && this.state.useTooltip) {
            if (resize) resize();
            this.props.onHover();
            return this.setState(
                () => ({ showTooltip: true }),
                () => {
                    this.setState(() => ({
                        tooltipWidth: this.tooltip.current?.getBoundingClientRect().width || 0,
                    }));
                },
            );
        }
        return null;
    };

    onMouseLeave = () => {
        if (this.state.useTooltip) this.setState(() => ({ showTooltip: false }));
    };

    onClick = (e) => {
        if (!this.state.useModal || this.state.showModal || this.props.noModal) {
            // don't care, or already open
            return;
        }
        this.props.onClick();
        e.preventDefault();
        e.stopPropagation();

        this.setState(() => {
            return {
                showModal: true,
            };
        });
    };

    onHideModal = (e) => {
        // no event is generated when a Lightbox is the child and its X is clicked
        if (e) {
            e.stopPropagation();
        }

        this.setState(() => {
            return {
                showModal: false,
            };
        });
    };

    _buildQueryMap() {
        const { breakpoint } = this.props;

        return {
            useModal: {
                maxWidth: breakpoint,
            },
            useTooltip: {
                minWidth: breakpoint + 1,
            },
        };
    }

    onElementQueryChange = (queryResults) => {
        this.setState(() => {
            return queryResults;
        });
    };

    render() {
        const {
            children,
            className,
            noHover,
            noModal,
            placement,
            contentClassName,
        } = this.props;
        const {
            showTooltip,
            style,
            useTooltip,
            useModal,
            showModal,
            queries,
        } = this.state;

        const inputChildren = React.Children.map(children, (child) => {
            if (!child) return null;
            if (child.type.displayName === TooltipSelect.displayName) {
                return React.cloneElement(child, {
                    getPosition: this.getPosition,
                    onMouseEnter: this.onMouseEnter,
                    onMouseLeave: this.onMouseLeave,
                    onClick: this.onClick,
                    useTooltip: useTooltip,
                    useModal: useModal,
                    showModal: showModal,
                    noModal: noModal,
                    noHover: noHover,
                });
            }

            return (
                <ElementQuery
                    element={this.queryElement}
                    onChange={this.onElementQueryChange}
                    passAsProps={false}
                    queries={queries}
                >
                    <React.Fragment>
                        {!noHover && useTooltip && showTooltip
                                && ReactDOM.createPortal(
                                    <div
                                        className={classNames(
                                            styles.tooltipContainer,
                                            className,
                                            {
                                                [styles.top]: placement === 'top',
                                                [styles.bottom]: placement === 'bottom',
                                            },
                                        )}
                                        onMouseEnter={this.onMouseEnter}
                                        onMouseLeave={this.onMouseLeave}
                                        onTouchEnd={(e) => e.stopPropagation()}
                                        ref={this.tooltip}
                                        role="tooltip"
                                        style={style}
                                    >
                                        <div className={classNames(
                                            styles.tooltipContent,
                                            contentClassName,
                                            {
                                                [styles.top]: placement === 'top',
                                                [styles.bottom]: placement === 'bottom',
                                            },
                                        )}>
                                            {React.cloneElement(child)}
                                        </div>
                                    </div>,
                                    document.body,
                                )}
                        {!noModal && useModal && showModal && (
                            <Modal>
                                <Lightbox
                                    className={className}
                                    onHide={this.onHideModal}
                                >
                                    {React.cloneElement(child)}
                                </Lightbox>
                            </Modal>
                        )}
                    </React.Fragment>
                </ElementQuery>
            );
        });
        return inputChildren;
    }
}

export {
    TooltipController,
    getLeftOffset,
};

export default TooltipController;
